import React, { useEffect, useMemo, useState } from 'react';
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Divider,
    Stack,
    Chip,
    Paper,
} from '@mui/material';
import { Close as CloseIcon, Info as InfoIcon } from '@mui/icons-material';

const formatValue = (value) => {
    if (value === null || value === undefined || value === '') {
        return 'N/A';
    }
    if (typeof value === 'object') {
        try {
            return JSON.stringify(value, null, 2);
        } catch (error) {
            return 'N/A';
        }
    }
    return String(value);
};

const AnalyzerRowDrawer = () => {
    const [open, setOpen] = useState(false);
    const [row, setRow] = useState(null);

    useEffect(() => {
        window.showAnalyzerRowDrawer = (payload) => {
            if (!payload) {
                return;
            }
            setRow(payload);
            setOpen(true);
        };

        return () => {
            if (window.showAnalyzerRowDrawer) {
                delete window.showAnalyzerRowDrawer;
            }
        };
    }, []);

    const enrichmentSummary = useMemo(() => {
        if (!row) {
            return 'N/A';
        }
        return row.enrichment_label || row.enrichment || row.enrichment_status || 'N/A';
    }, [row]);

    const externalEnhancement = useMemo(() => {
        if (!row || !row.extra || typeof row.extra !== 'object') {
            return null;
        }

        return row.extra.external_enhancement || null;
    }, [row]);

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 480, md: 560 },
                    bgcolor: 'background.default',
                },
            }}
        >
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        p: 2,
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <InfoIcon fontSize="small" />
                        <Typography variant="h6" component="h2">
                            Analyzer Details
                        </Typography>
                    </Stack>
                    <IconButton onClick={handleClose} sx={{ color: 'inherit' }} size="small">
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                    {!row ? (
                        <Typography color="text.secondary">No data selected.</Typography>
                    ) : (
                        <Stack spacing={2}>
                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                    <Chip label={formatValue(row.item)} size="small" color="primary" />
                                    <Chip label={formatValue(row.analyze_name || row.name)} size="small" />
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    {formatValue(row.datetime || row.time)}
                                </Typography>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Status
                                </Typography>
                                <Typography variant="body2">{formatValue(enrichmentSummary)}</Typography>
                                {row.enrichment_last_error && (
                                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                        {formatValue(row.enrichment_last_error)}
                                    </Typography>
                                )}
                            </Paper>

                            {row.special_details && (
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Enhanced Analyzer
                                    </Typography>
                                    <Typography component="pre" sx={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', m: 0 }}>
                                        {formatValue(row.special_details)}
                                    </Typography>
                                </Paper>
                            )}

                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Payload
                                </Typography>
                                <Typography component="pre" sx={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', m: 0 }}>
                                    {formatValue(row.enrichment_payload)}
                                </Typography>
                            </Paper>

                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Result
                                </Typography>
                                <Typography component="pre" sx={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', m: 0 }}>
                                    {formatValue(row.enrichment_result)}
                                </Typography>
                            </Paper>

                            {externalEnhancement && (
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        External Enhancement
                                    </Typography>
                                    <Typography component="pre" sx={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', m: 0 }}>
                                        {formatValue(externalEnhancement)}
                                    </Typography>
                                </Paper>
                            )}

                            <Paper variant="outlined" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Extra
                                </Typography>
                                <Typography component="pre" sx={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', m: 0 }}>
                                    {formatValue(row.extra)}
                                </Typography>
                            </Paper>

                            {row.image_url && (
                                <Paper variant="outlined" sx={{ p: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Image
                                    </Typography>
                                    <Box component="a" href={row.image_url} target="_blank" rel="noreferrer">
                                        <Box
                                            component="img"
                                            src={row.image_thumb_url || row.image_url}
                                            alt="Analyzer"
                                            sx={{ width: '100%', borderRadius: 1 }}
                                        />
                                    </Box>
                                </Paper>
                            )}
                        </Stack>
                    )}
                </Box>
            </Box>
        </Drawer>
    );
};

export default AnalyzerRowDrawer;
